import React, { FC, useState } from 'react'
import classNames from 'classnames'
import { snakeCase } from 'lodash'
import { SvgIcon } from 'components/icon'
import { Label } from 'components/label/Label'
import { Text } from 'components/ui-kit'

import './Input.scss'

type InputType = 'text' | 'number' | 'password'
type InputSize = 'regular' | 'small'

interface Props {
  type?: InputType
  value?: any
  onChange?: (value: any) => void
  placeholder?: string
  min?: number
  max?: number
  maxLength?: number
  label?: string
  disabled?: boolean
  error?: string
  hint?: string
  className?: string
  size?: InputSize
  icon?: string
  hasClearIcon?: boolean
  isRequired?: boolean
  onClear?: () => void
  validationPattern?: (value: any) => boolean
  setInputRef?: (ref: HTMLElement | null) => void
  isCanSeePassword?: boolean
}

export const Input: FC<Props> = ({
  type = 'text',
  value,
  onChange,
  placeholder = '',
  min,
  max,
  maxLength,
  label,
  disabled = false,
  error = '',
  hint = '',
  className = '',
  size = 'regular',
  icon,
  hasClearIcon = false,
  onClear,
  validationPattern = null,
  setInputRef,
  isRequired,
  isCanSeePassword = false,
}) => {
  const [inputType, setInputType] = useState(type)

  const handleShowPassword = () => {
    if (inputType === 'password') {
      setInputType('text')
    } else {
      setInputType('password')
    }
  }

  const containerClassName = classNames(
    'input-cmp',
    {
      withIcon: !!icon,
      withClearIcon: hasClearIcon,
      disabled,
      error: !!error,
      small: size === 'small',
    },
    className,
  )

  const inputId = label ? `${snakeCase(label)}-input` : undefined

  return (
    <div className={containerClassName}>
      {label && (
        <label className="input-header" htmlFor={inputId}>
          <Label isRequired={isRequired}>{label}</Label>
        </label>
      )}
      <div className="input-wrapper">
        <input
          id={inputId}
          type={inputType}
          value={value}
          placeholder={placeholder}
          min={min}
          max={max}
          onChange={e =>
            validationPattern ? validationPattern(e.target.value) && onChange(e.target.value) : onChange(e.target.value)
          }
          maxLength={maxLength}
          disabled={disabled}
          ref={setInputRef}
        />
        {icon && <SvgIcon name={icon} className="icon" height={14} width={14} />}
        {hasClearIcon && value && !disabled && (
          <span className={classNames('icon-wrapper', 'clear-icon')} onClick={onClear}>
            <SvgIcon name="cancel" height={10} width={10} />
          </span>
        )}
        {isCanSeePassword && (
          <span className="showPassword" onClick={handleShowPassword}>
            <SvgIcon name="show" />
          </span>
        )}
      </div>
      {error && <div className="error-block">{error}</div>}
      {!error && hint && (
        <Text type="hint" className="hint-block">
          {hint}
        </Text>
      )}
    </div>
  )
}
